// How meter-day failure stages appear to people. The classic-observability
// meterday job classifies every registered Classic meter once per cycle day
// into one stage; this module groups stages into the plain-language groups
// CX and Support use, with an owner and next step for each. The loader stores
// the group key as analytics.meter_day.stage_group, so group keys are stable
// identifiers: add new ones, never rename.

/**
 * Stage codes in the job's ladder order (`STAGES` in
 * classic-observability jobs/meterday/meterday/classify.py). The first
 * matching test wins, so the order is also the diagnosis order.
 */
export const METER_DAY_STAGES = [
  'ok',
  'superseded_registration',
  'miss_no_cellular',
  'miss_carrier_unobserved',
  'provisioning_not_in_account',
  'provisioning_inactive',
  'no_attach',
  'attached_unprobed',
  'attached_no_data',
  'headend_contact_no_read',
  'sync_not_processed',
  'sql_stale',
] as const;

export type MeterDayStage = typeof METER_DAY_STAGES[number];

export type MeterHealthGroup =
  | 'reading'
  | 'sim_not_provisioned'
  | 'not_connecting'
  | 'connected_no_data'
  | 'received_not_processed'
  | 'not_observable'
  | 'duplicate_registration'
  | 'unknown';

/** Semantic colour; the frontend maps it to its own classes. */
export type MeterHealthTone = 'ok' | 'warning' | 'critical' | 'info' | 'neutral';

/** Who usually fixes it. */
export type MeterHealthOwner = 'none' | 'ops' | 'field' | 'engineering' | 'support' | 'data';

export interface MeterHealthGroupInfo {
  group: MeterHealthGroup;
  label: string;
  tone: MeterHealthTone;
  owner: MeterHealthOwner;
  /** What the group usually means, for tooltips and the docs page. */
  meaning: string;
  /** What CX can tell the customer. */
  customerMessage: string;
  /** Replaces customerMessage when the meter is in a regional event (several customers, one area). */
  regionalCustomerMessage?: string;
  /** Internal next step. */
  nextStep: string;
  /** Data-hygiene groups that customer-facing counts leave out. */
  hiddenFromCustomerCounts: boolean;
}

/** Display order: reading first, then the actionable failures, then the rest. */
export const METER_HEALTH_GROUP_ORDER: ReadonlyArray<MeterHealthGroup> = [
  'reading',
  'sim_not_provisioned',
  'not_connecting',
  'connected_no_data',
  'received_not_processed',
  'not_observable',
  'duplicate_registration',
  'unknown',
];

export const METER_HEALTH_GROUPS: Readonly<Record<MeterHealthGroup, MeterHealthGroupInfo>> = {
  reading: {
    group: 'reading',
    label: 'Reading',
    tone: 'ok',
    owner: 'none',
    meaning: 'The read arrived for the cycle day.',
    customerMessage: 'The meter is reading normally.',
    nextStep: 'None.',
    hiddenFromCustomerCounts: false,
  },
  sim_not_provisioned: {
    group: 'sim_not_provisioned',
    label: 'SIM not provisioned',
    tone: 'warning',
    owner: 'ops',
    meaning: 'The SIM is not active, or is missing from the carrier account (ThingSpace).',
    customerMessage:
      "The meter's cellular service isn't active. We're fixing it on our side; no action needed from you.",
    nextStep: 'Ops: activate or add the SIM in ThingSpace.',
    hiddenFromCustomerCounts: false,
  },
  not_connecting: {
    group: 'not_connecting',
    label: 'Not connecting',
    tone: 'critical',
    owner: 'field',
    meaning:
      'The SIM did not reach the network that day: power, antenna, coverage, or a carrier outage when many meters in one area are affected.',
    customerMessage:
      "These meters haven't connected recently. Common causes are power, a damaged antenna or coverage at the pit.",
    regionalCustomerMessage:
      'Meters in your area stopped connecting because of a network issue affecting several utilities. Reads will backfill when service returns.',
    nextStep:
      'Field visit or customer site check; check RSSI history for weak signal. If regional, Ops confirms with the carrier.',
    hiddenFromCustomerCounts: false,
  },
  connected_no_data: {
    group: 'connected_no_data',
    label: 'Connected, no data',
    tone: 'info',
    owner: 'engineering',
    meaning: 'The meter reached the network, but nothing reached Metron.',
    customerMessage:
      "The meters are reaching the network but the reads aren't arriving. Our engineers are looking at it.",
    nextStep: 'Engineering: device or network path. Include the meter-day stage in the ticket.',
    hiddenFromCustomerCounts: false,
  },
  received_not_processed: {
    group: 'received_not_processed',
    label: 'Received, not processed',
    tone: 'info',
    owner: 'engineering',
    meaning: 'The data reached the headend, but the read did not land in WaterScope Classic.',
    customerMessage:
      "The meters are reaching us but the reads aren't being processed. Our engineers are looking at it.",
    nextStep: 'Engineering: Classic pipeline. Include the meter-day stage in the ticket.',
    hiddenFromCustomerCounts: false,
  },
  not_observable: {
    group: 'not_observable',
    label: 'Not observable',
    tone: 'neutral',
    owner: 'support',
    meaning:
      'The read was missed, but there is no network view for this meter: no cellular ICCID (for example FSK or Spectrum Wave), or a non-Verizon SIM.',
    customerMessage:
      "This meter's read didn't arrive. We're checking its read history to find out why.",
    nextStep: 'Standard troubleshooting: read history and call-in cadence.',
    hiddenFromCustomerCounts: false,
  },
  duplicate_registration: {
    group: 'duplicate_registration',
    label: 'Duplicate registration',
    tone: 'neutral',
    owner: 'data',
    meaning: 'A stale registration of a SIM that read under another registration the same day.',
    customerMessage: 'No action needed; this is a duplicate record on our side.',
    nextStep: 'Data hygiene: remove the stale registration.',
    hiddenFromCustomerCounts: true,
  },
  unknown: {
    group: 'unknown',
    label: 'Unknown',
    tone: 'neutral',
    owner: 'support',
    meaning: 'A stage this version of Wellspring does not recognise yet.',
    customerMessage: "We're checking why this meter's read didn't arrive.",
    nextStep: 'Check the raw stage code and update the stage mapping.',
    hiddenFromCustomerCounts: false,
  },
};

const STAGE_GROUP: Readonly<Record<MeterDayStage, MeterHealthGroup>> = {
  ok: 'reading',
  superseded_registration: 'duplicate_registration',
  miss_no_cellular: 'not_observable',
  miss_carrier_unobserved: 'not_observable',
  provisioning_not_in_account: 'sim_not_provisioned',
  provisioning_inactive: 'sim_not_provisioned',
  no_attach: 'not_connecting',
  attached_unprobed: 'connected_no_data',
  attached_no_data: 'connected_no_data',
  headend_contact_no_read: 'received_not_processed',
  sync_not_processed: 'received_not_processed',
  sql_stale: 'received_not_processed',
};

/** Short per-stage labels for tooltips and the meter strip. */
export const METER_DAY_STAGE_LABELS: Readonly<Record<MeterDayStage, string>> = {
  ok: 'Read arrived',
  superseded_registration: 'Read under another registration',
  miss_no_cellular: 'Missed, no cellular ID',
  miss_carrier_unobserved: 'Missed, carrier not observable',
  provisioning_not_in_account: 'SIM missing from carrier account',
  provisioning_inactive: 'SIM not active',
  no_attach: 'No network connection that day',
  attached_unprobed: 'Connected, not checked further',
  attached_no_data: 'Connected, nothing reached us',
  headend_contact_no_read: 'Reached the headend, no read',
  sync_not_processed: 'Received, not synced',
  sql_stale: 'Synced, not updated',
};

const KNOWN_STAGES = new Set<string>(METER_DAY_STAGES);

export function isMeterDayStage(stage: string): stage is MeterDayStage {
  return KNOWN_STAGES.has(stage);
}

/** The group for a stage code; unrecognised codes map to `unknown` rather than throwing. */
export function stageGroup(stage: string): MeterHealthGroup {
  return isMeterDayStage(stage) ? STAGE_GROUP[stage] : 'unknown';
}

export interface MeterDayStageDescription {
  /** The raw code, kept so an unrecognised stage is still visible. */
  stage: string;
  known: boolean;
  label: string;
  info: MeterHealthGroupInfo;
}

export function describeStage(stage: string): MeterDayStageDescription {
  if (isMeterDayStage(stage)) {
    return {
      stage,
      known: true,
      label: METER_DAY_STAGE_LABELS[stage],
      info: METER_HEALTH_GROUPS[STAGE_GROUP[stage]],
    };
  }
  return {
    stage,
    known: false,
    label: stage.trim() || 'No stage',
    info: METER_HEALTH_GROUPS.unknown,
  };
}
